import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import UserAvatar from '../../../../../shared/components/general/UserAvatar';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';
import { color_defaults_white, color_shades_dark, voicify_spark_light, voicify_spark_lightest } from '../../../../../shared/constants/colors';

interface PlatformAvatarUploadProps {
    storageKey: string;
    displayName: string;
    disabled?: boolean;
    onError?: (errors: string[]) => void;
    onClearError?: () => void;
}

const MAX_FILE_SIZE = 1024 * 1024;

const PlatformAvatarUpload: React.FC<PlatformAvatarUploadProps> = ({
    storageKey,
    displayName,
    disabled = false,
    onError,
    onClearError
}) => {
    const [customAvatarUrl, setCustomAvatarUrl] = useState<string | null>(() => {
        try {
            return localStorage.getItem(storageKey);
        } catch {
            return null;
        }
    });
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const avatarInputRef = useRef<HTMLInputElement>(null);

    const initials = useMemo(() => {
        if (!displayName) return 'PLT';
        return displayName
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 3);
    }, [displayName]);

    const handleAvatarClick = () => {
        if (!disabled) {
            avatarInputRef.current?.click();
        }
    };

    const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            onError?.(['Invalid file type. Please select a JPG, PNG, GIF, or WebP image.']);
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            onError?.(['Image is too large. Please select an image under 1MB for the platform avatar.']);
            return;
        }

        onClearError?.();
        setIsUploadingAvatar(true);

        try {
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => reject(new Error('Unable to read image file.'));
                reader.readAsDataURL(file);
            });

            localStorage.setItem(storageKey, dataUrl);
            setCustomAvatarUrl(dataUrl);
        } catch (error: any) {
            onError?.([error?.message || 'Failed to save the platform avatar. Please try a smaller image.']);
        } finally {
            setIsUploadingAvatar(false);
            if (avatarInputRef.current) {
                avatarInputRef.current.value = '';
            }
        }
    };

    return (
        <AvatarSection>
            <input
                type="file"
                ref={avatarInputRef}
                onChange={handleAvatarFileChange}
                accept="image/*"
                style={{ display: 'none' }}
                disabled={disabled}
            />
            {isUploadingAvatar ? (
                <AvatarLoaderWrapper>
                    <AvatarRing />
                    <AvatarInnerRing />
                    <AvatarLoaderContent>
                        <SecondaryLoader className="grey" />
                    </AvatarLoaderContent>
                </AvatarLoaderWrapper>
            ) : (
                <ClickableAvatarWrapper onClick={handleAvatarClick} disabled={disabled} title={disabled ? undefined : 'Click to change profile picture'}>
                    <AvatarRing />
                    <AvatarInnerRing />
                    <UserAvatar
                        firstName={displayName || 'P'}
                        lastName=""
                        src={customAvatarUrl}
                        size={175}
                        noShadow={true}
                        noImageText={initials}
                    />
                    {!disabled && (
                        <AvatarUploadOverlay>
                            <AvatarUploadIcon viewBox="0 0 24 24">
                                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                            </AvatarUploadIcon>
                            <AvatarUploadText>Change</AvatarUploadText>
                        </AvatarUploadOverlay>
                    )}
                </ClickableAvatarWrapper>
            )}
        </AvatarSection>
    );
};

const AvatarSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AvatarLoaderWrapper = styled.div`
    position: relative;
    width: 215px;
    height: 215px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AvatarLoaderContent = styled.div`
    position: absolute;
    width: 175px;
    height: 175px;
    border-radius: 50%;
    background: ${color_defaults_white};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
`;

const ClickableAvatarWrapper = styled.div<{ disabled?: boolean }>`
    position: relative;
    width: 215px;
    height: 215px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.55 : 1};

    &::before {
        content: '';
        position: absolute;
        width: 175px;
        height: 175px;
        border-radius: 50%;
        background: ${color_defaults_white};
        z-index: 1;
    }

    > *:not(:first-child):not(:nth-child(2)) {
        z-index: 2;
    }

    &:hover > div:last-of-type {
        opacity: 1;
    }
`;

const AvatarRing = styled.div`
    position: absolute;
    width: 195px;
    height: 195px;
    border-radius: 50%;
    background: ${voicify_spark_lightest};
    opacity: 0.6;
`;

const AvatarInnerRing = styled.div`
    position: absolute;
    width: 183px;
    height: 183px;
    border-radius: 50%;
    border: 2px solid ${voicify_spark_light};
    background: transparent;
`;

const AvatarUploadOverlay = styled.div`
    position: absolute;
    width: 175px;
    height: 175px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 10;
    pointer-events: none;
`;

const AvatarUploadIcon = styled.svg`
    width: 32px;
    height: 32px;
    fill: ${color_defaults_white};
    margin-bottom: 4px;
`;

const AvatarUploadText = styled.span`
    color: ${color_defaults_white};
    font-size: 12px;
    font-weight: 600;
`;

export default PlatformAvatarUpload;
